import type { AssignedAgent } from '../types/contact.types';
import type { FormattedUser } from '../hooks/graphql/users/useOrganizationUsers';

/**
 * Resolve the assigned-agent display data for a contact.
 *
 * `users` is the organisation's roster, every status included — an invited
 * member who has not signed in yet is still a real owner. Membership in it is
 * a condition, not merely a fallback source: the backend snapshot is trusted
 * only when its agent is on the roster, because `assigned_agent_id` outlives
 * the people it points at. A contact owned by someone no longer on the team
 * would otherwise show their name in the column while being unreachable
 * through the Assigned To filter, which offers only the roster.
 *
 * Returns `null` when the agent is absent or off the roster — callers render
 * "-", which is the truth: nobody on this team owns this contact.
 */
export function resolveAssignedAgent(
  assignedAgent: AssignedAgent | null | undefined,
  assignedAgentId: string | null | undefined,
  users: FormattedUser[]
): AssignedAgent | null {
  const id = assignedAgent?.agentId ?? assignedAgentId;
  if (!id) return null;
  if (!users.some((user) => user.id === id)) return null;

  if (assignedAgent) return assignedAgent;

  if (assignedAgentId) {
    const u = users.find((user) => user.id === assignedAgentId);
    if (u) {
      return {
        agentId: u.id,
        firstName: u.firstName,
        lastName: u.lastName,
        email: u.email ?? null,
        phone: u.phone_number ?? null,
        photoUrl: u.photoUrl ?? null,
      };
    }
  }

  return null;
}

/** Format an AssignedAgent to a trimmed "First Last" string. Empty when absent. */
export function formatAssignedAgentName(agent: AssignedAgent | null | undefined): string {
  if (!agent) return '';
  return `${agent.firstName ?? ''} ${agent.lastName ?? ''}`.trim();
}
